import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActionPlanCategoriesQuery } from 'services/actionPlan/actionPlanCategories';
import { ActionPlanOutcomesCategory } from 'types/ActionPlan';

export interface ProcessedActionPlanData {
  steps: { stepNumber: number; title: string }[];
  dietary: string[];
  dietaryTotal: number;
  lifestyle: string[];
  lifestyleTotal: number;
  supplements: string[];
  supplementsTotal: number;
}

const processActionPlanData = (
  rows: ActionPlanOutcomesCategory[],
): ProcessedActionPlanData => {
  const stepMap = new Map<
    number,
    { title: string; rows: ActionPlanOutcomesCategory[] }
  >();

  rows.forEach(row => {
    const stepNumber = row.action_category_number ?? 0;
    const title = row.action_category_text ?? '';

    if (!stepMap.has(stepNumber)) {
      stepMap.set(stepNumber, { title, rows: [] });
    }
    stepMap.get(stepNumber)!.rows.push(row);
  });

  const steps = Array.from(stepMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([stepNumber, { title }]) => ({
      stepNumber,
      title: title.replace(/^Step \d+:\s*/, ''),
    }));

  const dietary: string[] = [];
  const lifestyle: string[] = [];
  const supplements: string[] = [];
  const seenActions = new Set<number>();

  rows.forEach(row => {
    const outcome = row.desired_outcomes;
    const rec = outcome?.action_plan_recommendation as any;
    if (!rec || !rec.display_title) return;

    const recId = rec.id as number | undefined;
    if (recId == null || seenActions.has(recId)) return;
    seenActions.add(recId);

    const category = rec.category as string | undefined;
    if (category === 'DietarySuggestions') {
      dietary.push(rec.display_title);
    } else if (category === 'LifestyleSuggestions') {
      lifestyle.push(rec.display_title);
    } else if (category === 'SupplementSuggestions') {
      supplements.push(rec.display_title);
    }
  });

  return {
    steps: steps.slice(0, 3),
    dietary: dietary.slice(0, 3),
    dietaryTotal: dietary.length,
    lifestyle: lifestyle.slice(0, 3),
    lifestyleTotal: lifestyle.length,
    supplements: supplements.slice(0, 3),
    supplementsTotal: supplements.length,
  };
};

export const useProcessedActionPlanData = (): ProcessedActionPlanData => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { data: actionPlanData } = useQuery<
    ActionPlanOutcomesCategory[] | null
  >(getActionPlanCategoriesQuery(currentKitId));

  const processedData = useMemo(() => {
    return processActionPlanData(actionPlanData ?? []);
  }, [actionPlanData]);

  return processedData;
};
